import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazy, useEffect } from 'react';
import { keycloak, createLoginUrl } from '@coverright/shared/keycloak';
import { RouteErrorBoundary } from '@coverright/ui/shared';
import App from './app';
import { QuizLayout } from '../shared';
import { Zip } from '../pages/zip';
import { BasicInfo } from '../pages/basic-info';
import { PersonalInfo } from '../pages/personal-info';

// Only the pages above (the pre-auth quiz entry) are bundled eagerly, since
// they're what a fresh visitor's first paint waits on. Everything below is
// only reachable after that funnel, so it's split into its own chunk.
const lazyNamed = <T extends Record<string, any>>(factory: () => Promise<T>, name: keyof T) =>
  lazy(async () => ({ default: (await factory())[name] }));

const AnalyzingInfo = lazyNamed(() => import('../pages/analyzing-info'), 'AnalyzingInfo');
const Situation = lazyNamed(() => import('../pages/situation'), 'Situation');
const Doctors = lazyNamed(() => import('../pages/doctors'), 'Doctors');
const Drugs = lazyNamed(() => import('../pages/drugs'), 'Drugs');
const SpecialProgramPage = lazyNamed(() => import('../pages/special-program'), 'SpecialProgramPage');
const Payment = lazyNamed(() => import('../pages/payment'), 'Payment');
const PlanType = lazyNamed(() => import('../pages/plan-type'), 'PlanType');
const Pharmacy = lazyNamed(() => import('../pages/pharmacy'), 'Pharmacy');
const PlansSimple = lazyNamed(() => import('../pages/plans/simple'), 'PlansSimple');
const PlansSimpleLayout = lazyNamed(() => import('../pages/plans/simple'), 'PlansSimpleLayout');
const PlanTypeSimple = lazyNamed(() => import('../pages/plans/simple'), 'PlanTypeSimple');
const PlansAdvanced = lazyNamed(() => import('../pages/plans/advanced'), 'PlansAdvanced');
const PlansAdvancedMa = lazyNamed(() => import('../pages/plans/advanced'), 'PlansAdvancedMa');
const PlansAdvancedMg = lazyNamed(() => import('../pages/plans/advanced'), 'PlansAdvancedMg');
const PlansAdvancedPdp = lazyNamed(() => import('../pages/plans/advanced'), 'PlansAdvancedPdp');
const PlansComparison = lazyNamed(() => import('../pages/plans/advanced'), 'PlansComparison');
const ProgressTracker = lazyNamed(() => import('../pages/progress-tracker/progress-tracker'), 'ProgressTracker');
const Learn = lazyNamed(() => import('../pages/learn'), 'Learn');
const LearnVideo = lazyNamed(() => import('../pages/learn'), 'Video');
const Spotower = lazyNamed(() => import('../pages/spotower'), 'Spotower');

const ProtectedRoute = ({ fallbackUrl }: { fallbackUrl?: string }) => {
  useEffect(() => {
    if (!keycloak.authenticated) {
      window.location.href = fallbackUrl ?? createLoginUrl();
    }
  }, [fallbackUrl]);

  if (!keycloak.authenticated) return null;
  return <Outlet />;
};

const basePath = '/';

const routes = {
  zip: 'zip',
  basicInfo: 'basic-info',
  personalInfo: 'personal-info',
  analyzingInfo: 'analyzing-info',
  situation: 'situation',
  doctors: 'doctors',
  drugs: 'drugs',
  specialProgram: 'special-program',
  payment: 'payment',
  planType: 'plan-type',
  pharmacy: 'pharmacy',
  plans: 'plans',
  plan: 'plan',
  plansSimple: 'plans-simple',
  plansSimplePlanType: 'plans-simple/plan-type',
  plansSimplePlans: 'plans-simple/plans',
  plansAdvanced: 'plans-advanced',
  plansComparison: 'plans-comparison',
  progressTracker: 'progress-tracker',
  learn: 'learn',
  spotower: 'spotower',
};

export const getRoute = (key: keyof typeof routes): string =>
  basePath + routes[key];

export const router = createBrowserRouter([
  {
    path: basePath,
    element: <App />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: routes.analyzingInfo,
            element: <AnalyzingInfo />,
          },
          {
            path: routes.plansSimple,
            element: <PlansSimpleLayout />,
            children: [
              {
                path: 'plan-type',
                element: <PlanTypeSimple />,
              },
              {
                path: 'plans',
                element: <PlansSimple />,
              },
              {
                path: '',
                element: <Navigate to={'plan-type'} replace={true} />,
              },
              {
                path: '*',
                element: <Navigate to={'plan-type'} replace={true} />,
              },
            ],
          },
          {
            path: routes.plansAdvanced,
            element: <PlansAdvanced />,
            children: [
              {
                path: 'ma',
                element: <PlansAdvancedMa />,
              },
              {
                path: 'mg',
                element: <PlansAdvancedMg />,
              },
              {
                path: 'pdp',
                element: <PlansAdvancedPdp />,
              },
              {
                path: '',
                element: <Navigate to={'ma'} replace={true} />,
              },
              {
                path: '*',
                element: <Navigate to={'ma'} replace={true} />,
              },
            ]
          },
          {
            path: routes.progressTracker,
            element: <ProgressTracker />,
          },
          {
            path: routes.learn + '/:id',
            element: <LearnVideo />,
          },
          {
            path: routes.learn,
            element: <Learn />,
          },
          {
            path: routes.plans,
            element: <Navigate to={getRoute('plansSimple')} replace={true} />,
          },
          {
            path: routes.plansComparison,
            element: <PlansComparison />,
          },
        ]
      },
      {
        path: routes.spotower,
        element: <Spotower />,
      },
      {
        path: '*',
        element: <QuizLayout />,
        children: [
          {
            path: routes.zip,
            element: <Zip />,
          },
          {
            path: routes.basicInfo,
            element: <BasicInfo />,
          },
          {
            path: routes.personalInfo,
            element: <PersonalInfo />,
          },
          {
            element: <ProtectedRoute />,
            children: [
              {
                path: routes.situation,
                element: <Situation />,
              },
              {
                path: routes.doctors,
                element: <Doctors />,
              },
              {
                path: routes.drugs,
                element: <Drugs />,
              },
              {
                path: routes.pharmacy,
                element: <Pharmacy />,
              },
              {
                path: routes.specialProgram,
                element: <SpecialProgramPage />,
              },
              {
                path: routes.payment,
                element: <Payment />,
              },
              {
                path: routes.planType,
                element: <PlanType />,
              },
            ]
          },
        ]
      },
      {
        path: '',
        element: <Navigate to={routes.zip} replace={true} />,
      },
      {
        path: '*',
        element: <Navigate to={routes.zip} replace={true} />,
      },
    ],
  },
  {
    path: '',
    element: <Navigate to={getRoute('zip')} replace={true} />,
  },
  {
    path: '*',
    element: <Navigate to={getRoute('zip')} replace={true} />,
  },
]);
